import type { GraphqlSubscriptionMessage } from '@aws-amplify/api-graphql';
import { Subscription } from 'rxjs';
import { Hub } from '@aws-amplify/core';
import { backoffPause } from '../utils/async';
import { getSubscription } from '../api/backend-events';
import { VinceApp } from '../composables/usePermission';
import { useUserStore } from './userStore.js';
import type { BackendNotification, OnEventPublished } from 'vince-core/types/notifications';

const MAX_RETRIES = 5;
const UNKNOWN_VALUE = 'unknown';
export const BROADCAST = 'vince:broadcast';
const BACKEND = 'vince:backend';

export const useBackendEventsStore = defineStore('backendEventsStore', () => {
    const { getJWT } = useUserStore();

    let currentRetryCount = 1;
    let currentSubscription: Subscription;

    const initSubscription = async () => {
        if (currentSubscription) {
            currentSubscription.unsubscribe();
        }

        const token = await getJWT();
        const subscriptionQueryResult = getSubscription(token!);

        currentSubscription = subscriptionQueryResult.subscribe({
            next: (message: GraphqlSubscriptionMessage<OnEventPublished>) => {
                const rawEventData = message.data.onEventPublished;

                rawEventData.resourceId = rawEventData.resourceId ?? UNKNOWN_VALUE;
                rawEventData.action = rawEventData.action ?? UNKNOWN_VALUE;

                const eventName = `${rawEventData.app}:${rawEventData.resource}:${rawEventData.resourceId}:${rawEventData.action}`;
                const data = rawEventData.data ? JSON.parse(rawEventData.data) : {};
                const keys = rawEventData.keys ? JSON.parse(rawEventData.keys) : (data.keys ? data.keys : {});

                const eventData = {
                    ...rawEventData,
                    ...{
                        data,
                        keys,
                        eventName
                    }
                } as BackendNotification;

                if (eventName.startsWith(BROADCAST)) {
                    Hub.dispatch(BROADCAST, {
                        event: eventName,
                        data: eventData,
                        message: BROADCAST
                    }, BACKEND);
                } else if (eventData.app === VinceApp.AI_APPS) {
                    // For ai-apps with compound resourceId (e.g., appId:sessionId), dispatch to session-scoped channel
                    const specificChannel = `${eventData.app}:${eventData.resource}:${eventData.resourceId}`;
                    Hub.dispatch(specificChannel, {
                        event: eventName,
                        data: eventData,
                        message: specificChannel
                    }, BACKEND);
                } else {
                    const channel = `${eventData.app}:${eventData.resource}`;

                    Hub.dispatch(channel, {
                        event: eventName,
                        data: eventData,
                        message: channel
                    }, BACKEND);
                }
            },
            error: (errorValue: Error) => {
                reInitSubscription(errorValue);
            }
        });
    };

    const ensureSubscription = async () => {
        if (!currentSubscription || (currentSubscription && currentSubscription.closed)) {
            await initSubscription();
        }
    };

    const reInitSubscription = async (errorValue: Error) => {
        if (currentRetryCount > MAX_RETRIES) {
            // eslint-disable-next-line no-console
            console.error('Giving up to establish backend subscription: ', errorValue);
            return;
        }

        await backoffPause(currentRetryCount, 2);
        currentRetryCount++;
        await initSubscription();
    };

    return { ensureSubscription };
});
